import { ReactNode, useEffect, useRef, useState } from "react";
import { FieldErrors, UseFormHandleSubmit } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, useMediaQuery } from "@mui/material";

import { Section, SectionBox } from "components/atoms";
import {
  FormControlButtons,
  FormControlButtonsMobile,
  Summary,
} from "components/molecules";
import { FormDataModel } from "helpers/global-types";
import { stepHistoryHelper } from "helpers/navigation";
import { useTypedTheme } from "hooks";
import { useStoreActions, useStoreState } from "store/store";

type Props = {
  children: ReactNode;
  step: "step-1" | "step-2" | "step-3" | "step-4" | "step-5" | "step-6";
  onSubmit: (data: FormDataModel) => void;
  handleSubmit: UseFormHandleSubmit<FormDataModel, undefined>;
  disableNextButton?: boolean;
  // Steps that fetch critical data on mount (e.g. step-2 vehicles, step-5
  // countries) pass their readiness here so the transition loader stays up
  // until that data is present. Defaults to true (ready once the step paints).
  contentReady?: boolean;
};

const PageWrapper = ({
  children,
  handleSubmit,
  onSubmit,
  step,
  disableNextButton = false,
  contentReady = true,
}: Props) => {
  // styles
  const {
    theme: {
      breakpoints: { down },
    },
  } = useTypedTheme();
  const isMobile = useMediaQuery(down("md"));
  // actions
  const setIsGlobalLoading = useStoreActions((a) => a.setIsGlobalLoading);
  const setLoadingMessage = useStoreActions((a) => a.setLoadingMessage);
  const setNavPending = useStoreActions((a) => a.setNavPending);
  const setCartError = useStoreActions((a) => a.setCartError);

  // What to shimmer onscreen while the form's onSubmit runs. Each step
  // does meaningfully different work on Next — looking up vehicles,
  // saving trip details, etc. — so a generic "Loading…" is the wrong
  // texture. Step-6 is handled by step-6 itself (local loading) and
  // never hits this map.
  const nextStepLoadingMessage: Record<Props["step"], string> = {
    "step-1": "Finding rides for your route…",
    "step-2": "Locking in your vehicle…",
    "step-3": "Saving your trip details…",
    "step-4": "Reviewing your selections…",
    "step-5": "Almost there — preparing checkout…",
    "step-6": "Finalizing your booking…",
  };
  // state
  const isCustomDestination = useStoreState((s) => s.isCustomDestination);
  const isGlobalLoading = useStoreState((state) => state.isGlobalLoading);
  const navPending = useStoreState((state) => state.navPending);
  const selectedCar = useStoreState((s) => s.selectedCar);
  const navigate = useNavigate();
  const location = useLocation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const refSectionBox = useRef<any>();
  const [sectionBoxHeight, setSectionBoxHeight] = useState(0);
  // True on the wrapper instance that STARTED the current transition, so its
  // own readiness effect below skips (it must not clear the loader before we
  // navigate). The destination step mounts a fresh instance (ref false) and
  // owns the clear.
  const initiatedNavRef = useRef(false);
  // Tracks whether this instance is still mounted, for the no-navigation
  // fallback in nextHandle (e.g. a validation error leaves us on the step).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Hide the transition loader from the DESTINATION side. When a Next click set
  // navPending, whichever step wrapper mounts next (fresh instance, so
  // initiatedNavRef is false) clears the loader once its content is ready — so
  // the overlay lifts exactly when the step is usable instead of on a fixed
  // 500ms timer. Timers are scoped to this mount, so rapid navigation can't
  // cross-clear a later transition. Page-load loading (useTokenCart) and
  // in-step loading (step-6 payment) never set navPending, so they're untouched.
  useEffect(() => {
    if (!navPending || initiatedNavRef.current) return;
    if (contentReady) {
      const raf = requestAnimationFrame(() => {
        setIsGlobalLoading(false);
        setNavPending(false);
      });
      return () => cancelAnimationFrame(raf);
    }
    // Content not ready yet — cap the wait so a slow or failed fetch never
    // traps the user behind the overlay.
    const timeout = setTimeout(() => {
      setIsGlobalLoading(false);
      setNavPending(false);
    }, 2500);
    return () => clearTimeout(timeout);
  }, [navPending, contentReady, setIsGlobalLoading, setNavPending]);

  // Surface a blocked submit. Previously this only console.warn'd, so a
  // failing field-level rule made the Next button look inert: the loader
  // flashed and nothing else happened, with no indication of which field was
  // at fault. That silence is the shape of "I tried twice and gave up".
  //
  // Prefer the first rule's own message (e.g. "This time has already passed")
  // since it names the actual problem; fall back to a generic prompt.
  const onError = (errors: FieldErrors<FormDataModel>) => {
    // eslint-disable-next-line no-console
    console.warn("error submitting", errors);

    const firstMessage = Object.values(errors)
      .map((e) => (e as { message?: string } | undefined)?.message)
      .find((m): m is string => typeof m === "string" && m.length > 0);

    setCartError({
      code: "form_validation",
      message:
        firstMessage ??
        "Please check the highlighted fields before continuing.",
      retryable: true,
    });
  };

  const nextHandle = () => {
    // INFO step-6 has local loading
    if (step === "step-6") {
      handleSubmit(onSubmit, onError)();
      return;
    }
    if (isGlobalLoading) return;
    setLoadingMessage(nextStepLoadingMessage[step]);
    // Mark this instance as the initiator so its readiness effect skips, then
    // flag the transition. The destination step's wrapper clears the loader
    // once it's ready (see the effect above) — no fixed timer.
    initiatedNavRef.current = true;
    setNavPending(true);
    handleSubmit(onSubmit, onError)().finally(() => {
      // Fallback for the no-navigation case (e.g. a validation error): if we're
      // still mounted shortly after submit resolved, no destination will clear
      // the loader we optimistically showed, so clear it here. On a successful
      // transition this component has unmounted (mountedRef false) and the
      // destination owns the clear. Checked-mounted so a stale timer from an
      // earlier transition can never clear a later one.
      setTimeout(() => {
        if (mountedRef.current) {
          setIsGlobalLoading(false);
          setNavPending(false);
        }
      }, 150);
    });
  };

  const backHandle = () => {
    switch (step) {
      case "step-1":
        break;
      case "step-2":
      case "step-3":
      case "step-4":
      case "step-5":
      case "step-6":
        stepHistoryHelper.prev(
          navigate,
          isCustomDestination,
          location.pathname,
          selectedCar?.isShuttle
        );
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    setSectionBoxHeight(refSectionBox.current.clientHeight);
  });

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          paddingTop: [2, 2, 4, 6, 8],
          paddingBottom: [2, 2, 4, 6, 8],
          paddingLeft: [0, 2, 2, 4, 6, 8],
          paddingRight: [0, 2, 2, 4, 6, 8],
          justifyContent: "space-between",
          alignItems: "stretch",
          gap: [0, 4],
        }}
      >
        <Section>
          <SectionBox step={step} refSectionBox={refSectionBox}>
            {children}
          </SectionBox>
        </Section>
        {!isMobile && (
          <Summary
            sectionBoxHeight={sectionBoxHeight}
            plugForFirstStep={step === "step-1"}
            oneSeatAllowed={
              step === "step-3" ? selectedCar?.oneSeatAllowed : false
            }
          />
        )}
      </Box>
      {!isMobile ? (
        <FormControlButtons
          backHandle={backHandle}
          nextHandle={nextHandle}
          nextButtonText={step === "step-6" ? "Go to Checkout" : "Next"}
          disableBackButton={step === "step-1"}
          disableNextButton={disableNextButton}
        />
      ) : (
        <FormControlButtonsMobile
          plugForFirstStep={step === "step-1"}
          page6Variant={step === "step-6"}
          backHandle={backHandle}
          nextHandle={nextHandle}
          nextButtonText={step === "step-6" ? "Checkout" : ""}
          disableBackButton={step === "step-1"}
          disableNextButton={disableNextButton}
        />
      )}
    </>
  );
};

export default PageWrapper;
